import { Injectable } from '@angular/core'
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs'
import { LocalStorageService } from '../common/local-storage.service'
import { CommonService } from '../common/common.service'
import { HttpHandler, HttpHeaders, HttpRequest } from '@angular/common/http'
import { WebService } from '../api/web.service'
import { GlobalConstants } from 'src/constants/global-constants'
import { Login } from 'src/models/model'
import { ServiceUrlConstants } from 'src/constants/service-url-constants'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static readonly TOKEN_KEY = 'auth-token'
  private static readonly REFRESH_TOKEN_KEY = 'refresh'
  constructor(private cs: CommonService, private webService: WebService) {}

  addRefreshHeader = (headers: HttpHeaders) => {
    if (!headers) {
      headers = new HttpHeaders()
    }
    const token = AuthService.getRefreshToken()
    headers = headers.set('refreshToken', `${token}`)
    return headers
  }

  public static getToken(): string {
    return LocalStorageService.get(AuthService.TOKEN_KEY)
  }

  public static getRefreshToken(): string {
    return LocalStorageService.get(AuthService.REFRESH_TOKEN_KEY)
  }

  public static setToken(token: string) {
    LocalStorageService.set(AuthService.TOKEN_KEY, token)
  }
  public static setRefreshToken(token: string) {
    LocalStorageService.set(AuthService.REFRESH_TOKEN_KEY, token)
  }
  public static deleteToken() {
    LocalStorageService.remove(AuthService.TOKEN_KEY)
    LocalStorageService.remove(AuthService.REFRESH_TOKEN_KEY)
  }

  clearLocalStorageAndRedirectToLogin() {
    AuthService.deleteToken()
    this.cs.routeTo(GlobalConstants.ROUTING.LOGIN)
  }

  redirectToLogin() {
    this.cs.routeTo(GlobalConstants.ROUTING.LOGIN)
  }

  isUserAuthenticated(): boolean {
    const token = AuthService.getToken()
    if (!token) {
      return false
    }
    return true
  }

  ssoLogin(access_token: string): Observable<Login> {
    return this.webService
      .postWithoutAuth(ServiceUrlConstants.API_AUTH_SSO_LOGIN, access_token, null, [], false, true)
      .pipe(map((data: Login) => data))
  }

  supportUserLogin(token: string) {
    AuthService.setRefreshToken(token)
    let headers = new HttpHeaders()
    return this.webService.getWithRefresh(ServiceUrlConstants.API_AUTH_TOKEN_REFRESH, null, headers)
  }

  refreshToken = (request: HttpRequest<any>, next: HttpHandler) => {
    let headers = new HttpHeaders()
    return this.webService.getWithRefresh(ServiceUrlConstants.API_AUTH_TOKEN_REFRESH, null, headers).pipe(
      switchMap((refreshTokenResponse: any) => {
        AuthService.setToken(refreshTokenResponse.token)
        AuthService.setRefreshToken(refreshTokenResponse.refreshToken)
        return next.handle(
          request.clone({
            setHeaders: {
              Authorization: `Bearer ${refreshTokenResponse.token}`
            }
          })
        )
      }),
      catchError(error => {
        if (JSON.parse(error).status === 401) {
          this.clearLocalStorageAndRedirectToLogin()
          this.cs.showError('Session expired. Please login again')
        } else {
          this.cs.showError('Something went wrong')
        }
        return of()
      })
    )
  }

  logout = () => {
    const headers = new HttpHeaders()
    return this.webService.postWithAuthTokenAndRefresh(ServiceUrlConstants.API_AUTH_LOGOUT, null, headers)
  }
}
